import React, { useMemo } from "react";

import {
  GeneralInformation,
  PlanRates,
  STDBenefits,
  StdCoverage,
  StdPlan,
} from "../../constants/aquafina";
import ContributionType from "../sections/contribution-type-section";
import { RangeIterator } from "../../utils";
import Rates from "../rates";
import CollapsableSection from "../sections/collapsable-section";
import { CoverageRow, NotProvidedRow } from "../table/rows";
import { CoverageCell } from "../table/cells";
import { ViewMode } from "../../constants/types";
import { VIEW_MODES } from "../../constants";
import PolicyGeneralInfo from "../sections/policy-general-info";
import ProposalGeneralInfo from "../sections/proposal-general-info";

type Props = {
  viewMode: ViewMode;
  coverage: StdCoverage;
  displayV4Fields: boolean;
};

const Std = ({ coverage, viewMode, displayV4Fields }: Props) => {
  const state = useMemo(() => {
    const benefitDetails: STDBenefits[] = [];
    const generalInfo: GeneralInformation[] = [];
    const contributionTypes = new Map<string, string[]>();
    const rates: PlanRates[] = [];
    const plans: StdPlan[] = [];
    let inconsistentRateData = false;
    RangeIterator(coverage.plans.length, (index) => {
      const plan = coverage.plans[index];
      benefitDetails.push(plan.benefitDetails);
      generalInfo.push(plan.generalInformation);
      rates.push(plan.planRates);
      plans.push(plan);
      if (plan.inconsistentRateData === true) {
        inconsistentRateData = true;
      }
    });
    return {
      benefitDetails,
      generalInfo,
      contributionTypes,
      rates,
      plans,
      inconsistentRateData,
    };
  }, [coverage]);

  return (
    <>
      {viewMode === VIEW_MODES.POLICY ? (
        <>
          <PolicyGeneralInfo data={state.generalInfo} />
          <ContributionType plans={state.plans} />
        </>
      ) : (
        <ProposalGeneralInfo data={state.generalInfo} />
      )}
      <STDBenefitDetails
        benefits={state.benefitDetails}
        displayV4Fields={displayV4Fields}
      />
      <Rates
        rates={state.rates}
        inconsistentRateData={state.inconsistentRateData}
        totalPlans={
          state.rates.length === 0 ? state.plans.length : state.rates.length
        }
      />
    </>
  );
};

type STDBenefitDetailsProps = {
  displayV4Fields: boolean;
  benefits: STDBenefits[];
};

const STDBenefitDetails = ({
  benefits,
  displayV4Fields,
}: STDBenefitDetailsProps) => (
  <CollapsableSection numberOfColumns={benefits.length} title="Benefit Details">
    <STDBenefitDetailsData
      benefits={benefits}
      displayV4Fields={displayV4Fields}
    />
  </CollapsableSection>
);

const STDBenefitDetailsData = ({
  benefits,
  displayV4Fields,
}: STDBenefitDetailsProps) => {
  if (benefits.every((b) => b === null)) {
    return <NotProvidedRow colSpan={benefits.length} />;
  }

  return (
    <>
      <CoverageRow label="Benefit Amount" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].benefitAmount} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Maximum Amount">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].maximumAmount} key={index} />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <CoverageRow label="Definition of Disability" isWhite>
          {RangeIterator(benefits.length, (index) => (
            <CoverageCell
              value={benefits[index].definitionOfDisability}
              key={index}
            />
          ))}
        </CoverageRow>
      ) : null}
      <CoverageRow
        label="Elimination Period - Accident"
        isWhite={!displayV4Fields}
      >
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].eliminationPeriodAccident}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow
        label="Elimination Period - Illness"
        isWhite={displayV4Fields}
      >
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].eliminationPeriodIllness}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Benefit Duration" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].benefitDuration} key={index} />
        ))}
      </CoverageRow>
      <CoverageRow label="Pre-existing Condition" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].preExistingCondition}
            key={index}
          />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow
            label="Definition of Earnings"
            isWhite={!displayV4Fields}
          >
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].definitionOfEarnings}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="FICA Match" isWhite={displayV4Fields}>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].isFicaMatchIncluded}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow
            label="Billable or Non-Billable"
            isWhite={!displayV4Fields}
          >
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].isFicaMatchBillable}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
    </>
  );
};

export default Std;
